import React, { useState, useEffect } from 'react';
import { getSpeakers } from '../api/client';
import { useSettings } from '../contexts/SettingsContext';
import { User } from 'lucide-react';
import { thumbnailImage } from '../utils/imageOptimizer';

export const SpeakersSection = () => {
  const { settings } = useSettings();
  const [speakers, setSpeakers] = useState([]);
  const [filter, setFilter] = useState('all');
  const primaryColor = settings.colors?.primary || '#0E7A5F';

  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const data = await getSpeakers();
        setSpeakers(data.speakers || []);
      } catch { /* Section gracefully hides when no speakers available */ }
    };
    fetchSpeakers();
  }, []);

  if (speakers.length === 0) return null;

  const subEvents = [...new Set(speakers.map(s => s.sub_event).filter(Boolean))];
  const filtered = filter === 'all' ? speakers : speakers.filter(s => s.sub_event === filter);

  return (
    <section id="speakers" className="py-20 bg-white" data-testid="speakers-section">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Our Speakers
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Industry leaders, policy makers, and visionaries shaping the future of trade and investment
          </p>
        </div>

        {subEvents.length > 1 && (
          <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="speaker-filters">
            <button
              onClick={() => setFilter('all')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filter === 'all'
                  ? 'text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === 'all' ? { backgroundColor: primaryColor } : {}}
            >
              All Speakers
            </button>
            {subEvents.map(ev => (
              <button
                key={ev}
                onClick={() => setFilter(ev)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === ev
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={filter === ev ? { backgroundColor: primaryColor } : {}}
              >
                {ev}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {filtered.map((speaker) => (
            <div
              key={speaker.id}
              className="group text-center"
              data-testid={`speaker-card-${speaker.id}`}
            >
              <div className="relative w-40 h-40 mx-auto mb-5 rounded-full overflow-hidden bg-gray-100 ring-4 ring-gray-100 group-hover:ring-green-100 transition-all duration-300">
                {speaker.photo ? (
                  <img
                    src={thumbnailImage(speaker.photo)}
                    alt={speaker.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                    <User size={48} className="text-gray-400" />
                  </div>
                )}
                {speaker.sub_event && (
                  <div
                    className="absolute bottom-0 left-0 right-0 text-white text-xs py-1 font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {speaker.sub_event}
                  </div>
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{speaker.name}</h3>
              {speaker.title && (
                <p className="text-sm font-medium" style={{ color: primaryColor }}>{speaker.title}</p>
              )}
              {speaker.company && (
                <p className="text-sm text-gray-500 mt-1">{speaker.company}</p>
              )}
              {speaker.country && (
                <p className="text-xs text-gray-400 mt-1">{speaker.country}</p>
              )}
              {speaker.bio && (
                <p className="text-sm text-gray-600 mt-3 line-clamp-3 leading-relaxed">{speaker.bio}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
